import React from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';
import Preview from '../wrapper';

interface Address {
  firstName?: string;
  lastName?: string;
  streetNumber?: string;
  streetName?: string;
  county?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}
export interface Props {
  showAddressMatchHelpText?: boolean;
}

const AddressesPreview: React.FC<Props> = ({ showAddressMatchHelpText = true }) => {
  const { data } = useCart();
  const shippingAddress = data?.shippingAddress ?? {};
  const billingAddress = data?.billingAddress ?? {};
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });

  const addressPart = (...parts: (string | undefined)[]): string => parts.filter(Boolean).join(', ');

  const AddressDisplay = (address: Address) => {
    const addressLine1 = addressPart(address.firstName, address.lastName);
    const addressLine2 = addressPart(address.streetNumber, address.streetName, address.county);
    const addressLine3 = addressPart(address.postalCode, address.city, address.country);

    return (
      <p className="text-14 font-light leading-5 text-secondary-black">
        {addressLine1 && (
          <>
            {addressLine1}
            <br />
          </>
        )}
        {addressLine2 && (
          <>
            {addressLine2}
            <br />
          </>
        )}
        {(address.postalCode || address.city) && <>{addressLine3}</>}
      </p>
    );
  };

  return (
    <Preview>
      <div className="border-neutral-400 lg:border lg:p-16">
        {showAddressMatchHelpText && (
          <div className={`relative mb-18 flex items-center py-15 pl-50 pr-15 text-sm font-light text-black`}>
            <i className="icon icon-info absolute left-10 text-32 !font-light text-[#17a2b8]"></i>
            <span className="font-light">
              {formatCheckoutMessage({
                id: 'address.match.info',
                defaultMessage:
                  'To help us process your order please ensure your billing address matches the address the payment card is registered to.',
              })}
            </span>
          </div>
        )}

        <div className="border-l-4 border-ag_color-text_green py-18 pl-18">
          <div className="block gap-10 md:flex">
            <div className="w-full md:w-[40%]">
              <div className="mb-12 text-14 font-medium">
                {data?.custom?.deliveryOption !== 'StoreCollection'
                  ? formatCheckoutMessage({
                      id: 'shippingAddress',
                      defaultMessage: 'Shipping Address',
                    })
                  : formatCheckoutMessage({
                      id: 'your.selected.store',
                      defaultMessage: 'Your selected store',
                    })}
              </div>
              {AddressDisplay(shippingAddress)}
            </div>
            <div className="mt-30 w-full md:mt-auto md:w-[40%]">
              <div className="mb-12 text-14 font-medium">
                {formatCheckoutMessage({
                  id: 'billingAddress',
                  defaultMessage: 'Billing Address',
                })}
              </div>
              {AddressDisplay(billingAddress)}
            </div>
          </div>
          {}
        </div>
      </div>
    </Preview>
  );
};

export default AddressesPreview;
